import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  User, History, HelpCircle, FileText, Shield, Gift, Users, Bookmark,
  ArrowRight, LogOut, Pencil, Check, X as Cancel, Loader2,
  Handshake, BadgeCheck, UploadCloud, Clock3,
} from "lucide-react";
import EnableNotificationsButton from "@/components/EnableNotificationsButton";

/**
 * Compte page — landing hub for the user's account area.
 * Lists all account-related shortcuts in clear cards.
 */
export default function Compte() {
  const { user, logout, updateProfile } = useAuth();
  const [referralDiscount, setReferralDiscount] = useState(5);

  useEffect(() => {
    api.get("/referral-config-public").then(({ data }) => {
      if (data?.discount_percent != null) setReferralDiscount(data.discount_percent);
    }).catch(() => {});
  }, []);

  const items = [
    { to: "/mes-transactions", icon: History, title: "Historique des transferts", desc: "Tous vos envois passés et leur statut." },
    { to: "/mes-envois", icon: History, title: "Mes envois récents", desc: "Historique local des transactions sur cet appareil." },
    { to: "/contacts", icon: Users, title: "Mes contacts", desc: "Gérer vos bénéficiaires et favoris pour aller plus vite." },
    { to: "/modeles", icon: Bookmark, title: "Mes modèles d'envoi", desc: "Envois récurrents nommés (ex: Loyer maman) lançables en 1 clic." },
    { to: "/parrainage", icon: Gift, title: "Code de parrainage", desc: `Invitez vos proches et faites-les économiser ${referralDiscount} %.` },
    { to: "/partenaire", icon: Handshake, title: "Partenaire engagé", desc: "Gagnez 2 % de commission sur les transferts France → Gabon que vous apportez." },
    { to: "/faq", icon: HelpCircle, title: "Support · FAQ", desc: "Questions fréquentes et assistance." },
    { to: "/mentions-legales", icon: FileText, title: "Mentions légales", desc: "Informations légales et obligations." },
    { to: "/conditions", icon: Shield, title: "Conditions d'utilisation", desc: "CGU et politique de confidentialité." },
  ];

  return (
    <div className="min-h-screen sl-hero-bg pt-28 pb-24">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-start justify-between flex-wrap gap-3 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[var(--sl-secondary-dark)]">
              Espace personnel
            </div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-2">Bonjour {user?.name || user?.email || ""}</h1>
            <p className="mt-2 text-gray-600 text-sm">Toutes vos informations et raccourcis Swiftlink-Africa.</p>
          </div>
          {user && (
            <button
              onClick={() => logout()}
              className="sl-btn-outline text-sm"
              data-testid="compte-logout"
            >
              <LogOut size={14} /> Se déconnecter
            </button>
          )}
        </div>

        {/* Mes informations card */}
        <div className="sl-card mb-6" data-testid="compte-profile">
          {user ? (
            <ProfileEditor user={user} updateProfile={updateProfile} />
          ) : (
            <>
              <div className="flex items-center gap-3 mb-3">
                <span className="w-10 h-10 rounded-full bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]">
                  <User size={18} />
                </span>
                <h3 className="font-heading font-semibold">Mes informations</h3>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                <span>Connectez-vous pour gérer votre profil.</span>
                <Link to="/login" className="sl-btn-primary text-xs"><User size={12} /> Connexion</Link>
                <Link to="/register" className="sl-btn-outline text-xs">Créer un compte</Link>
              </div>
            </>
          )}
        </div>

        {user && (
          <div className="sl-card mb-6 flex items-start gap-3 flex-wrap" data-testid="compte-notifications-card">
            <div className="flex-1 min-w-0">
              <h3 className="font-heading font-semibold">Notifications navigateur</h3>
              <p className="text-xs text-gray-500 mt-1">Recevez une notification dès qu&apos;un statut de vos transferts change (validé, envoyé, terminé).</p>
            </div>
            <EnableNotificationsButton />
          </div>
        )}

        {user && <VerificationCard />}

        {/* Menu grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <Link
              key={it.to}
              to={it.to}
              className="sl-card sl-card-hoverable flex items-start gap-3"
              data-testid={`compte-link-${it.to.replace(/\//g, "-")}`}
            >
              <span className="w-10 h-10 rounded-xl bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)] flex-shrink-0">
                <it.icon size={16} />
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-sm flex items-center gap-1">
                  {it.title} <ArrowRight size={12} className="text-gray-300" />
                </div>
                <div className="text-xs text-gray-500 mt-1">{it.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ============ Profile editor ============ */
const CORRIDOR_LABELS = {
  tg_ga: "🇹🇬 → 🇬🇦 Togo → Gabon",
  ga_fr: "🇬🇦 → 🇫🇷 Gabon → France",
  ga_rw: "🇬🇦 → 🇷🇼 Gabon → Rwanda",
  fr_ga: "🇫🇷 → 🇬🇦 France → Gabon",
};

function ProfileEditor({ user, updateProfile }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  const [preferredCorridor, setPreferredCorridor] = useState(user.preferred_corridor || "");
  const [saving, setSaving] = useState(false);

  const reset = () => {
    setName(user.name || "");
    setPhone(user.phone || "");
    setPreferredCorridor(user.preferred_corridor || "");
    setEditing(false);
  };

  const save = async () => {
    const trimmedName = (name || "").trim();
    if (!trimmedName) { toast.error("Le nom est obligatoire"); return; }
    setSaving(true);
    try {
      await updateProfile({
        name: trimmedName,
        phone: (phone || "").trim(),
        preferred_corridor: preferredCorridor || null,
      });
      toast.success("Profil mis à jour");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]">
            <User size={18} />
          </span>
          <h3 className="font-heading font-semibold">Mes informations</h3>
        </div>
        {!editing ? (
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-xs inline-flex items-center gap-1.5 text-[var(--sl-primary)] hover:underline"
            data-testid="compte-edit-toggle"
          >
            <Pencil size={12} /> Modifier
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="text-xs inline-flex items-center gap-1.5 rounded-full bg-emerald-600 text-white px-3 py-1.5 hover:bg-emerald-700 disabled:opacity-60"
              data-testid="compte-edit-save"
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Enregistrer
            </button>
            <button
              type="button"
              onClick={reset}
              className="text-xs inline-flex items-center gap-1.5 rounded-full border border-gray-200 text-gray-600 px-3 py-1.5 hover:bg-gray-50"
              data-testid="compte-edit-cancel"
            >
              <Cancel size={12} /> Annuler
            </button>
          </div>
        )}
      </div>

      {!editing ? (
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Field label="Nom" value={user.name || "—"} />
          <Field label="Email" value={user.email} />
          {(user.phone || editing) && <Field label="Téléphone" value={user.phone || "—"} />}
          {user.preferred_corridor && (
            <Field label="Corridor préféré" value={CORRIDOR_LABELS[user.preferred_corridor] || user.preferred_corridor} />
          )}
          {user.role === "admin" && (
            <Link to="/admin" className="sl-btn-primary text-xs col-span-full sm:col-span-1">
              Accès admin <ArrowRight size={12} />
            </Link>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-3 text-sm" data-testid="compte-edit-form">
          <div>
            <label className="sl-label">Nom complet</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="sl-input"
              data-testid="compte-input-name"
              maxLength={80}
            />
          </div>
          <div>
            <label className="sl-label">Email (non modifiable)</label>
            <input type="email" value={user.email} disabled className="sl-input opacity-70" />
          </div>
          <div className="sm:col-span-2">
            <label className="sl-label">Téléphone (optionnel)</label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+241 ou +228 ou +33…"
              className="sl-input"
              data-testid="compte-input-phone"
              maxLength={20}
            />
          </div>
          <div className="sm:col-span-2">
            <label className="sl-label">Corridor préféré (optionnel)</label>
            <select
              value={preferredCorridor}
              onChange={(e) => setPreferredCorridor(e.target.value)}
              className="sl-input"
              data-testid="compte-input-preferred-corridor"
            >
              <option value="">— Aucun (choisir à chaque envoi) —</option>
              {Object.entries(CORRIDOR_LABELS).map(([code, label]) => (
                <option key={code} value={code}>{label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Ce corridor sera pré-sélectionné au démarrage de vos envois.
            </p>
          </div>
        </div>
      )}
    </>
  );
}

function Field({ label, value }) {
  return (
    <div className="rounded-lg bg-[var(--sl-bg-alt)] px-3 py-2">
      <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
      <div className="text-sm font-medium text-gray-800 mt-0.5">{value || "—"}</div>
    </div>
  );
}

/* ============ Account verification (KYC at account level) ============ */
const DOC_OPTIONS = [
  { v: "cni", l: "Carte nationale d'identité" },
  { v: "passeport", l: "Passeport" },
  { v: "permis", l: "Permis de conduire" },
];

function VerificationCard() {
  const [verif, setVerif] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState("cni");
  const [fullName, setFullName] = useState("");
  const [docNumber, setDocNumber] = useState("");
  const [docPath, setDocPath] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/me/verification");
      setVerif(data);
    } catch (_e) { /* ignore */ }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/kyc", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocPath(data.path);
      toast.success("Pièce téléversée");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
    setUploading(false);
  };

  const submit = async () => {
    if (!docPath) { toast.error("Veuillez téléverser votre pièce d'identité."); return; }
    if ((fullName || "").trim().length < 2) { toast.error("Nom complet requis."); return; }
    if (!(docNumber || "").trim()) { toast.error("Numéro de la pièce requis."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/me/verification", {
        doc_path: docPath, doc_type: docType, full_name: fullName.trim(), doc_number: docNumber.trim(),
      });
      setVerif(data);
      toast.success("Demande envoyée ! Un administrateur va la vérifier.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
    setSaving(false);
  };

  if (loading) return null;

  const status = verif?.status || "none";

  if (verif?.account_verified) {
    return (
      <div className="sl-card mb-6 flex items-center gap-3 border-emerald-100 bg-emerald-50/60" data-testid="verification-verified">
        <span className="w-10 h-10 rounded-full bg-emerald-100 grid place-items-center text-emerald-700"><BadgeCheck size={20} /></span>
        <div>
          <h3 className="font-heading font-semibold text-emerald-800 flex items-center gap-2">Client vérifié <BadgeCheck size={16} /></h3>
          <p className="text-xs text-emerald-700/80 mt-0.5">Votre identité est confirmée. Vous pouvez débloquer le bonus des missions quotidiennes.</p>
        </div>
      </div>
    );
  }

  if (status === "pending") {
    return (
      <div className="sl-card mb-6 flex items-center gap-3 border-amber-100 bg-amber-50/60" data-testid="verification-pending">
        <span className="w-10 h-10 rounded-full bg-amber-100 grid place-items-center text-amber-700"><Clock3 size={20} /></span>
        <div>
          <h3 className="font-heading font-semibold text-amber-800">Vérification en cours</h3>
          <p className="text-xs text-amber-700/80 mt-0.5">Votre pièce d'identité est en cours d'examen par notre équipe.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="sl-card mb-6" data-testid="verification-form">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-10 h-10 rounded-full bg-[var(--sl-primary)]/10 grid place-items-center text-[var(--sl-primary)]"><BadgeCheck size={18} /></span>
        <div>
          <h3 className="font-heading font-semibold">Vérifier mon compte</h3>
          <p className="text-xs text-gray-500">Obtenez le badge « Client vérifié » (condition finale pour débloquer le bonus des missions).</p>
        </div>
      </div>
      {status === "rejected" && verif?.reason && (
        <p className="text-xs text-rose-600 mb-3">Demande précédente refusée : {verif.reason}. Vous pouvez renvoyer une pièce.</p>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="sl-label">Type de pièce</label>
          <select className="sl-input" value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="verif-doc-type">
            {DOC_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <div>
          <label className="sl-label">Numéro de la pièce</label>
          <input className="sl-input" value={docNumber} onChange={(e) => setDocNumber(e.target.value)} maxLength={40} data-testid="verif-doc-number" />
        </div>
        <div className="sm:col-span-2">
          <label className="sl-label">Nom complet (tel qu'inscrit sur la pièce)</label>
          <input className="sl-input" value={fullName} onChange={(e) => setFullName(e.target.value)} maxLength={80} data-testid="verif-full-name" />
        </div>
        <div className="sm:col-span-2">
          <label className="sl-label">Pièce d'identité (photo ou PDF)</label>
          <label className="flex items-center gap-2 cursor-pointer rounded-xl border border-dashed border-gray-300 px-4 py-3 text-sm text-gray-600 hover:bg-gray-50" data-testid="verif-upload">
            {uploading ? <Loader2 size={16} className="animate-spin" /> : docPath ? <Check size={16} className="text-emerald-600" /> : <UploadCloud size={16} />}
            {docPath ? "Pièce téléversée ✓ (cliquez pour remplacer)" : uploading ? "Téléversement…" : "Téléverser ma pièce d'identité"}
            <input type="file" accept="image/*,.pdf" className="hidden" onChange={onFile} />
          </label>
        </div>
      </div>
      <button onClick={submit} disabled={saving || uploading} className="sl-btn-primary w-full justify-center mt-4" data-testid="verif-submit">
        {saving ? <Loader2 size={16} className="animate-spin" /> : <BadgeCheck size={16} />} Envoyer pour vérification
      </button>
    </div>
  );
}
